import React, { useCallback, useState } from "react";

import Button from "veneer/Button";

import { useAppDispatch } from "cortex/ui/src/hooks";
import { createPromiseApiAction } from "cortex/ui/src/redux-store/api-sagas";
import {
  BULK_DOWNLOAD_DEVICE,
  BULK_DOWNLOAD_DEVICE_CUSTOM,
} from "cortex/ui/src/redux-store/devices/actions";
import { addErrorMessageAction } from "cortex/ui/src/redux-store/messages";
import {
  type ResponseLike,
  getErrorMessage,
} from "cortex/ui/src/util/response";

type BulkExportResponse = {
  data?: {
    data?: {
      url?: string;
    };
  };
};

interface BulkDeviceEditSampleProps {
  appParams?: unknown[];
  flags?: unknown[];
  isCustomConfig: boolean;
  properties?: unknown[];
  targets?: unknown[];
}

interface BulkCustomDownloadArgs {
  appParams?: unknown[];
  flags?: unknown[];
  properties?: unknown[];
  targets?: unknown[];
}

export default function BulkDeviceEditSample({
  appParams,
  flags,
  isCustomConfig,
  properties,
  targets,
}: BulkDeviceEditSampleProps) {
  const dispatch = useAppDispatch();
  const [downloading, setDownloading] = useState(false);

  const addErrorMessage = useCallback(
    (message: string) => {
      dispatch(addErrorMessageAction(message));
    },
    [dispatch],
  );

  const bulkDownload = useCallback(() => {
    return createPromiseApiAction(dispatch, BULK_DOWNLOAD_DEVICE);
  }, [dispatch]);

  const bulkCustomDownload = useCallback(
    ({ appParams, flags, properties, targets }: BulkCustomDownloadArgs) => {
      return createPromiseApiAction(dispatch, BULK_DOWNLOAD_DEVICE_CUSTOM, [
        appParams,
        flags,
        properties,
        targets,
      ]);
    },
    [dispatch],
  );

  const getBulkExportCsv = useCallback(async () => {
    const serviceArgs = {
      appParams,
      flags,
      properties,
      targets,
    };

    try {
      const res = (await (isCustomConfig
        ? bulkCustomDownload(serviceArgs)
        : bulkDownload())) as BulkExportResponse;
      const url = res.data?.data?.url;

      if (url) {
        window.open(url, "_parent");
        return;
      }

      addErrorMessage("Unable to export devices config: No devices found.");
    } catch (e: unknown) {
      addErrorMessage(
        `Unable to export devices config: ${getErrorMessage(
          e as Error | ResponseLike | null,
        )}`,
      );
    } finally {
      setDownloading(false);
    }
  }, [
    addErrorMessage,
    appParams,
    bulkCustomDownload,
    bulkDownload,
    flags,
    isCustomConfig,
    properties,
    targets,
  ]);

  const startDownload = useCallback(() => {
    setDownloading(true);
    getBulkExportCsv();
  }, [getBulkExportCsv]);

  return (
    <Button
      disabled={downloading}
      icon="download"
      loading={downloading}
      onClick={startDownload}
      variant="outlined"
    >
      Export Devices Configuration as .csv
    </Button>
  );
}
